import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Camera, Upload, Image as ImageIcon, Loader2, X, Trash2, Edit2, Save, Sparkles, User, FolderOpen, Move, Folder } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { CameraCaptureMulti } from "@/components/smartfile/CameraCaptureMulti";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { GalleryImageModal } from "./GalleryImageModal";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { FolderPopover } from "./FolderPopover";
import { FolderSelector } from "./FolderSelector";
import { MoveFolderPopover } from "./MoveFolderPopover";

interface GalleryItem {
  id: string;
  file_path: string;
  file_name: string;
  file_size: number | null;
  file_type: string;
  source: "upload" | "camera";
  ai_summary: string | null;
  professional_notes: string | null;
  created_at: string;
  published_at: string | null;
  folder: string | null;
}

interface PatientGalleryProps {
  patientId: string;
  onImageAdded?: () => void;
}

export function PatientGallery({ patientId, onImageAdded }: PatientGalleryProps) {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; filePath: string; fileName: string } | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<"summary" | "notes" | "date" | null>(null);
  const [editedSummary, setEditedSummary] = useState("");
  const [editedNotes, setEditedNotes] = useState("");
  const [editedDate, setEditedDate] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  // Carregar pasta selecionada do localStorage ao montar
  const [selectedFolder, setSelectedFolder] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(`patient_gallery_selected_folder_${patientId}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [folders, setFolders] = useState<Array<{ name: string; count: number }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Função para atualizar pasta selecionada e salvar no localStorage
  const handleFolderSelect = useCallback((folder: string | null) => {
    setSelectedFolder(folder);
    try {
      if (folder === null) {
        localStorage.removeItem(`patient_gallery_selected_folder_${patientId}`);
      } else {
        localStorage.setItem(`patient_gallery_selected_folder_${patientId}`, JSON.stringify(folder));
      }
    } catch (error) {
      console.error("Erro ao salvar pasta no localStorage:", error);
    }
  }, [patientId]);

  const loadGalleryItems = useCallback(async () => {
    try {
      // @ts-ignore - patient_gallery table not yet in types
      const { data, error } = await supabase
        .from("patient_gallery")
        .select("id, file_path, file_name, file_size, file_type, source, ai_summary, professional_notes, created_at, published_at, folder")
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setItems((data || []) as unknown as GalleryItem[]);
      loadFolders();
    } catch (error) {
      console.error("Erro ao carregar galeria:", error);
      toast.error("Erro ao carregar galeria");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  const loadFolders = useCallback(async () => {
    try {
      // Buscar todas as pastas da tabela de pastas
      // @ts-ignore - patient_gallery_folders table not yet in types
      const { data: foldersData, error: foldersError } = await supabase
        .from("patient_gallery_folders")
        .select("name")
        .eq("patient_id", patientId)
        .order("name", { ascending: true });

      if (foldersError) throw foldersError;

      // Buscar todas as imagens para contar por pasta
      // @ts-ignore - patient_gallery table not yet in types
      const { data: imagesData, error: imagesError } = await supabase
        .from("patient_gallery")
        .select("folder")
        .eq("patient_id", patientId);

      if (imagesError) throw imagesError;

      // Contar imagens por pasta
      const folderCounts: Record<string, number> = {};
      (imagesData || []).forEach((item) => {
        const folder = item.folder || null;
        if (folder) {
          folderCounts[folder] = (folderCounts[folder] || 0) + 1;
        }
      });

      // Criar array com todas as pastas (mesmo as vazias) e suas contagens
      const foldersArray = (foldersData || []).map((folder) => ({
        name: folder.name,
        count: folderCounts[folder.name] || 0,
      }));

      setFolders(foldersArray);

      // Validar pasta selecionada atual (verificar se ainda existe)
      if (selectedFolder) {
        const folderExists = foldersArray.some(f => f.name === selectedFolder);
        if (!folderExists) {
          // Pasta não existe mais, limpar do localStorage
          handleFolderSelect(null);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar pastas:", error);
    }
  }, [patientId, selectedFolder, handleFolderSelect]);

  useEffect(() => {
    loadGalleryItems();
  }, [loadGalleryItems]);

  // Filtrar itens por pasta selecionada
  const filteredItems = selectedFolder === null
    ? items
    : items.filter((item) => item.folder === selectedFolder);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      // Verificar se é imagem pelo MIME type ou pela extensão
      const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
      const isImageByType = file.type.startsWith("image/");
      const isImageByExt = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "dng"].includes(fileExt);
      
      if (!isImageByType && !isImageByExt) {
        toast.error(`${file.name} não é uma imagem válida`);
        continue;
      }
      await uploadImage(file, "upload");
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePhotoCapture = async (files: File[]) => {
    setShowCamera(false);
    for (const file of files) {
      await uploadImage(file, "camera");
    }
  };

  const uploadImage = async (file: File, source: "upload" | "camera") => {
    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // patient-docs RLS: primeiro segmento do path deve ser auth.uid() (igual DocumentUploadStep)
      const rawExt = file.name.includes(".") ? file.name.split(".").pop() : "";
      const safeExt =
        rawExt && /^[a-zA-Z0-9]+$/.test(rawExt) ? rawExt.toLowerCase() : "jpg";
      const fileName = `${user.id}/${patientId}/gallery/${Date.now()}_${Math.random().toString(36).substring(7)}.${safeExt}`;

      const { error: uploadError } = await supabase.storage
        .from("patient-docs")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Definir MIME type padrão para arquivos DNG ou quando file.type estiver vazio
      let fileType = file.type;
      if (!fileType || fileType === "") {
        const ext = file.name.split(".").pop()?.toLowerCase() || "";
        if (ext === "dng") {
          fileType = "image/x-adobe-dng"; // MIME type padrão para DNG
        } else {
          fileType = `image/${ext}`; // Fallback genérico
        }
      }

      // @ts-ignore - patient_gallery table not yet in types
      const { data: galleryItem, error: insertError } = await supabase
        .from("patient_gallery")
        .insert({
          patient_id: patientId,
          owner_id: user.id,
          file_path: fileName,
          file_name: file.name,
          file_size: file.size,
          file_type: fileType,
          source,
          published_at: new Date().toISOString(), // Definir data de publicação como agora por padrão
          folder: selectedFolder || null, // Usar a pasta selecionada na tab
        })
        .select()
        .single();

      if (insertError) throw insertError;

      toast.success("Imagem adicionada à galeria!");
      loadGalleryItems();
      
      // Notificar que uma nova imagem foi adicionada (para atualizar timeline)
      onImageAdded?.();

      // Gerar resumo com Gemini 1.5 Flash (em background, não bloqueia o upload)
      // @ts-ignore - galleryItem type not yet in types
      generateSummary(galleryItem?.id, file).catch((error) => {
        console.error("Erro ao gerar resumo em background:", error);
      });
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "object" &&
              error !== null &&
              "message" in error &&
              typeof (error as { message: unknown }).message === "string"
            ? (error as { message: string }).message
            : "Erro ao fazer upload";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const generateSummary = async (galleryItemId: string, file: File) => {
    try {
      // Converter arquivo para base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = async () => {
        try {
          const base64Image = reader.result as string;
          const base64Data = base64Image.split(",")[1];

          // Chamar edge function para gerar resumo
          const { data: summaryData, error: summaryError } = await supabase.functions.invoke(
            "analyze-gallery-image",
            {
              body: {
                imageBase64: base64Data,
                mimeType: file.type,
              },
            }
          );

          if (!summaryError && summaryData?.summary) {
            // Atualizar item com resumo
            // @ts-ignore - patient_gallery table not yet in types
            await supabase
              .from("patient_gallery")
              .update({ ai_summary: summaryData.summary })
              .eq("id", galleryItemId);
            
            // Recarregar itens
            loadGalleryItems();
          }
        } catch (error) {
          console.error("Erro ao gerar resumo:", error);
          // Não bloquear o upload se o resumo falhar
        }
      };
    } catch (error) {
      console.error("Erro ao processar imagem para resumo:", error);
    }
  };

  const handleDeleteClick = (id: string, filePath: string, fileName: string) => {
    setItemToDelete({ id, filePath, fileName });
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      // Deletar do storage
      await supabase.storage.from("patient-docs").remove([itemToDelete.filePath]);

      // Deletar do banco
      const { error } = await supabase
        .from("patient_gallery")
        .delete()
        .eq("id", itemToDelete.id);

      if (error) throw error;

      toast.success("Imagem excluída!");
      setItemToDelete(null);
      loadGalleryItems();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      toast.error("Erro ao excluir imagem");
    }
  };

  const handleStartEdit = (itemId: string, field: "summary" | "notes" | "date", currentValue: string | null) => {
    setEditingItemId(itemId);
    setEditingField(field);
    if (field === "summary") {
      setEditedSummary(currentValue || "");
    } else if (field === "notes") {
      setEditedNotes(currentValue || "");
    } else if (field === "date") {
      // Converter data para formato YYYY-MM-DD para input type="date" usando timezone local
      if (currentValue) {
        const dateObj = new Date(currentValue);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        setEditedDate(`${year}-${month}-${day}`);
      } else {
        setEditedDate("");
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingField(null);
    setEditedSummary("");
    setEditedNotes("");
    setEditedDate("");
  };

  const handleSaveEdit = async (itemId: string) => {
    if (!editingField) return;

    try {
      setIsSaving(true);
      const updateData: { ai_summary?: string | null; professional_notes?: string | null; published_at?: string | null; updated_at: string } = {
        updated_at: new Date().toISOString()
      };

      if (editingField === "summary") {
        updateData.ai_summary = editedSummary || null;
      } else if (editingField === "notes") {
        updateData.professional_notes = editedNotes || null;
      } else if (editingField === "date") {
        // Converter data do formato YYYY-MM-DD para ISO string usando timezone local
        if (editedDate) {
          const [year, month, day] = editedDate.split('-').map(Number);
          // Criar data no timezone local (não UTC)
          const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0);
          updateData.published_at = dateObj.toISOString();
        } else {
          updateData.published_at = null;
        }
      }

      const { error } = await supabase
        .from("patient_gallery")
        .update(updateData)
        .eq("id", itemId);

      if (error) throw error;

      const successMessages = {
        summary: "Resumo IA salvo!",
        notes: "Notas salvas!",
        date: "Data de publicação atualizada!"
      };
      toast.success(successMessages[editingField]);
      handleCancelEdit();
      loadGalleryItems();
      onImageAdded?.();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar");
    } finally {
      setIsSaving(false);
    }
  };

  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const getImageUrl = async (filePath: string, itemId: string) => {
    // Se já temos a URL em cache, retornar
    if (imageUrls[itemId]) {
      return imageUrls[itemId];
    }

    try {
      // Criar signed URL válida por 1 hora
      const { data, error } = await supabase.storage
        .from("patient-docs")
        .createSignedUrl(filePath, 3600);

      if (error) throw error;
      if (data?.signedUrl) {
        setImageUrls(prev => ({ ...prev, [itemId]: data.signedUrl }));
        return data.signedUrl;
      }
    } catch (error) {
      console.error("Erro ao obter URL da imagem:", error);
    }
    return null;
  };

  // Carregar URLs das imagens quando os itens mudarem
  useEffect(() => {
    const loadImageUrls = async () => {
      const urls: Record<string, string> = {};
      for (const item of items) {
        try {
          const { data, error } = await supabase.storage
            .from("patient-docs")
            .createSignedUrl(item.file_path, 3600);
          
          if (!error && data?.signedUrl) {
            urls[item.id] = data.signedUrl;
          }
        } catch (error) {
          console.error(`Erro ao carregar URL para ${item.id}:`, error);
        }
      }
      setImageUrls(urls);
    };

    if (items.length > 0) {
      loadImageUrls();
    }
  }, [items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleMoveImage = async (itemId: string, newFolder: string | null) => {
    try {
      setIsSaving(true);
      // @ts-ignore - patient_gallery table not yet in types
      const { error } = await supabase
        .from("patient_gallery")
        .update({ folder: newFolder })
        .eq("id", itemId);

      if (error) throw error;

      toast.success("Imagem movida!");
      loadGalleryItems();
      onImageAdded?.();
    } catch (error) {
      console.error("Erro ao mover imagem:", error);
      toast.error("Erro ao mover imagem");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Botões de ação e gerenciamento de pastas */}
      <div className="flex flex-wrap gap-3 items-start">
        <div className="flex gap-3 flex-1">
          <Button
            onClick={() => setShowCamera(true)}
            disabled={uploading}
            className="flex items-center gap-2"
          >
            <Camera className="h-4 w-4" />
            Tirar Foto
          </Button>
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Enviar Imagem
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.dng"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
        <FolderPopover
          patientId={patientId}
          selectedFolder={selectedFolder}
          onSelectFolder={handleFolderSelect}
          onFoldersChanged={() => {
            loadGalleryItems();
          }}
        />
      </div>

      {/* Grid de imagens */}
      {filteredItems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma imagem na galeria ainda</p>
            <p className="text-sm text-muted-foreground mt-2">
              Tire fotos ou envie imagens para começar
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map((item) => {
            const imageUrl = imageUrls[item.id];
            return (
              <Card key={item.id} className="overflow-hidden hover:shadow-md transition-shadow">
                <div className="relative aspect-square bg-muted group">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={item.file_name}
                      className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setSelectedImageId(item.id)}
                      onError={(e) => {
                        console.error("Erro ao carregar imagem:", item.file_path);
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  )}

                  {/* Botões de ação */}
                  <div className="absolute top-2 right-2 flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <MoveFolderPopover
                      patientId={patientId}
                      currentFolder={item.folder}
                      onSelectFolder={(folder) => {
                        handleMoveImage(item.id, folder);
                      }}
                      trigger={
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-8 w-8"
                          title="Mover para pasta"
                        >
                          <Move className="h-4 w-4" />
                        </Button>
                      }
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDeleteClick(item.id, item.file_path, item.file_name)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-4 space-y-3">
                  {/* Data de Publicação - Editável */}
                  <div className="pt-2 border-t">
                    <div className="text-xs font-medium text-foreground mb-1.5">
                      📅 Data de Publicação:
                    </div>
                    {editingItemId === item.id && editingField === "date" ? (
                      <div className="space-y-2">
                        <Input
                          type="date"
                          value={editedDate}
                          onChange={(e) => setEditedDate(e.target.value)}
                          className="text-xs h-8"
                          disabled={isSaving}
                          autoFocus
                        />
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            className="h-6 px-2"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(item.id)}
                            disabled={isSaving}
                            className="h-6 px-2"
                          >
                            {isSaving ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Save className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                        onClick={() => {
                          const dateToEdit = item.published_at || item.created_at;
                          handleStartEdit(item.id, "date", dateToEdit);
                        }}
                      >
                        {item.published_at 
                          ? format(new Date(item.published_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                          : format(new Date(item.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                        }
                        <Edit2 className="h-3 w-3 inline-block ml-1 opacity-50" />
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <span>{item.source === "camera" ? "📷 Câmera" : "📤 Upload"}</span>
                    {item.folder && (
                      <>
                        <span className="text-muted-foreground/50">•</span>
                        <span className="flex items-center gap-1">
                          <Folder className="h-3 w-3" />
                          {item.folder}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Resumo IA */}
                  <div className="pt-2 border-t">
                    <div className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1">
                      <Sparkles className="h-3 w-3 text-primary" />
                      Resumo IA:
                    </div>
                    {editingItemId === item.id && editingField === "summary" ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editedSummary}
                          onChange={(e) => setEditedSummary(e.target.value)}
                          placeholder="Digite o resumo da IA..."
                          className="min-h-[80px] text-xs"
                          disabled={isSaving}
                          autoFocus
                        />
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            className="h-6 px-2"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(item.id)}
                            disabled={isSaving}
                            className="h-6 px-2"
                          >
                            {isSaving ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Save className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : item.ai_summary ? (
                      <p 
                        className="text-xs text-muted-foreground leading-relaxed cursor-pointer hover:text-foreground transition-colors"
                        onClick={() => handleStartEdit(item.id, "summary", item.ai_summary)}
                      >
                        {item.ai_summary}
                      </p>
                    ) : (
                      <button
                        onClick={() => handleStartEdit(item.id, "summary", null)}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors italic"
                      >
                        Clique para adicionar resumo IA...
                      </button>
                    )}
                  </div>

                  {/* Notas do Profissional */}
                  <div className="pt-2 border-t">
                    <div className="text-xs font-medium text-blue-700 dark:text-blue-400 mb-1.5 flex items-center gap-1">
                      <User className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                      Considerações:
                    </div>
                    {editingItemId === item.id && editingField === "notes" ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editedNotes}
                          onChange={(e) => setEditedNotes(e.target.value)}
                          placeholder="Digite suas considerações..."
                          className="min-h-[80px] text-xs"
                          disabled={isSaving}
                          autoFocus
                        />
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            className="h-6 px-2"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(item.id)}
                            disabled={isSaving}
                            className="h-6 px-2"
                          >
                            {isSaving ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Save className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : item.professional_notes ? (
                      <p 
                        className="text-xs text-blue-900 dark:text-blue-100 leading-relaxed cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleStartEdit(item.id, "notes", item.professional_notes)}
                      >
                        {item.professional_notes}
                      </p>
                    ) : (
                      <button
                        onClick={() => handleStartEdit(item.id, "notes", null)}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors italic"
                      >
                        Clique para adicionar considerações...
                      </button>
                    )}
                  </div>
                  {/* Pasta */}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de visualização e edição */}
      {selectedImageId && (
        <GalleryImageModal
          isOpen={selectedImageId !== null}
          onClose={() => setSelectedImageId(null)}
          imageId={selectedImageId}
          patientId={patientId}
          onUpdate={() => {
            loadGalleryItems();
            onImageAdded?.();
          }}
        />
      )}

      {/* Modal de câmera */}
      {showCamera && (
        <CameraCaptureMulti
          onCapture={handlePhotoCapture}
          onCancel={() => setShowCamera(false)}
          patientId={patientId}
        />
      )}

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={itemToDelete !== null} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta imagem?
              {itemToDelete && (
                <span className="font-medium mt-2 block">
                  {itemToDelete.fileName}
                </span>
              )}
              <br />
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

